import clsx from 'clsx'
import { rem } from 'polished'
import * as React from 'react'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import SolidCircleIcon from 'LuxKit/Icons/solid/SolidCircleIcon'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Typography from 'LuxKit/Typography/Typography'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import noop from 'lib/function/noop'
import { mediaHoverable } from 'lib/theme/mediaQueries'

import type {
  AccommodationDayView,
  AccommodationMobileDayCellSelectHandler,
} from './AccommodationCalendar.types'

const Cell = styled(Clickable)`
  position: relative;
  min-height: ${rem(60)};
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  padding-block: ${rem(4)} 0;
  padding-inline: ${rem(2)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: background-color 0.2s;

  &.type-check-in, &.type-check-out {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    color: ${props => props.theme.palette.highlight.primary.normalContrast};
  }

  &.type-between {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
    color: ${props => props.theme.palette.highlight.primary.lightContrast};
  }

  &.type-none.variant-unavailable {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
      }
    }
    &:active {
      background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    }
  }
`

const DurationLabel = styled(StructureLabel)`
  position: absolute;
  inset-inline-end: -${rem(20)};
  top: ${rem(6)};
  z-index: 1;
  transform: translateY(-100%);
`

const FeaturedIcon = styled(SolidFireIcon)`
  position: absolute;
  top: 0;
  inset-inline-end: ${rem(2)};
`

const Content = styled(Typography)`
  opacity: 0;

  &.visible {
    opacity: 1;
  }
`

const DayRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(2)};
`

export type AccommodationCalendarDayCellType = 'check-in' | 'check-out' | 'between' | 'none'
export type AccommodationDayCellVariant = 'price-member' | 'price-cheapest' | 'price' | 'none' | 'sold-out-extra-action' | 'unavailable' | 'availability-available' | 'availability-search'
export type AccommodationDayCellFeature = 'special' | 'loyalty'

interface Props {
  /** The raw calendar data for a day */
  day: AccommodationDayView
  /**
   * Event for when the cell is clicked, provides the day object to the listener
   */
  onClick?: AccommodationMobileDayCellSelectHandler
  /**
   * The purpose the cell is for, determines which components
   * in the cell are shown
   */
  variant: AccommodationDayCellVariant
  /**
   * The 'state' the cell is currently in
   * Determines mostly how the *cell* looks
   */
  type: AccommodationCalendarDayCellType
  style?: React.CSSProperties
  /**
   * The duration of the stay
   */
  duration?: number
  /**
   * Users current loyalty tier
   * Passed in for performance reasons so each cell isn't listening
   * on state
   */
  tier?: App.LoyaltyTier
}

function AccommodationCalendarSmallDayCell(props: Props) {
  const { day, onClick = noop, variant, type, style, duration, tier } = props

  const intl = useIntl()

  const priceColour = useMemo<React.ComponentProps<typeof Caption>['colour']>(() => {
    if (variant === 'price-member') {
      return 'lux-plus'
    } else if (variant === 'price-cheapest' && type !== 'check-in') {
      return 'highlight-primary-dark'
    }
    return 'light-foreground-alt'
  }, [variant, type])

  const showContent = type !== 'between' && type !== 'check-out'
  const isAvailabilityMode = variant === 'availability-available' || variant === 'availability-search'
  const isSoldOutDayDisabled = !isAvailabilityMode && day.soldOut && !day.soldOutExtraActionText

  const price = day.pricing?.price ?? 0

  const durationLabel = useMemo(() => {
    if (!duration) return undefined
    if (day.durationUnit === 'day') {
      return intl.formatMessage(
        {
          id: 'nQiLIon4mVqv63kqN1E4',
          defaultMessage: '{count, plural, one {# day} other {# days}}',
          description: 'Duration label in days shown on the check-out day cell.',
        },
        { count: duration },
      )
    }
    if (day.durationUnit === 'night') {
      return intl.formatMessage(
        {
          id: 'Q622B2824YXjRjLom2Mq',
          defaultMessage: '{count, plural, one {# night} other {# nights}}',
          description: 'Duration label in nights shown on the check-out day cell.',
        },
        { count: duration },
      )
    }
    return undefined
  }, [duration, day.durationUnit, intl])

  return <Cell
    style={style}
    disabled={variant === 'unavailable' || isSoldOutDayDisabled}
    onClick={() => onClick(day)}
    className={clsx(
      `type-${type}`,
      `variant-${variant}`,
    )}
    data-testid={`day-${day.date}`}
  >
    {day.featured === 'special' && type !== 'between' && <FeaturedIcon colour="urgency" size="XXS" />}
    {day.featured === 'loyalty' && type !== 'between' && tier && <FeaturedIcon as={SolidCircleIcon} size="XXS" colour={`tier-${tier}`} />}
    {type === 'check-out' && !!duration && <DurationLabel variant="dark">
      {durationLabel}
    </DurationLabel>}
    {isAvailabilityMode && <DayRow>
      <BodyText variant="medium" align="center">{day.day}</BodyText>
      {variant === 'availability-available' && <SolidCheckCircleIcon colour="highlight-primary-dark" size="XS" />}
      {variant === 'availability-search' && <LineSearchIcon colour="light-foreground-alt" size="XS" />}
    </DayRow>}
    {!isAvailabilityMode && <BodyText variant="medium" align="center">{day.day}</BodyText>}
    <Content align="center" className={clsx({ visible: showContent })}>
      {(variant === 'price' || variant === 'price-cheapest' || variant === 'price-member') && <Caption
        variant="medium"
        colour={priceColour}
      >
        {day.conditionalPrice ?
            <FormattedMessage
              id="BubDwLd7a81-HsviPhBn"
              defaultMessage="{price}*"
              description="Calendar day cell price with asterisk for conditional pricing"
              values={{
                price: <FormatCurrency
                  value={price}
                  format="dynamic"
                  notation={price > 10000 ? 'compact' : undefined}
                  maximumFractionDigits={price > 10000 ? 1 : undefined}
                />,
              }}
            /> :
            <FormatCurrency
              value={price}
              format="dynamic"
              notation={price > 10000 ? 'compact' : undefined}
              maximumFractionDigits={price > 10000 ? 1 : undefined}
            />}
      </Caption>}
      {variant === 'sold-out-extra-action' && day.soldOutExtraActionText && <Caption
        variant="medium"
        colour="light-foreground-alt"
        underline
      >
        {day.soldOutExtraActionText}
      </Caption>}
    </Content>
  </Cell>
}

export default React.memo(AccommodationCalendarSmallDayCell)
